import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { fetchTariff, updateTariffPricebook } from "@/lib/api";
import { PricingForm } from "@/components/PricingForm";
import { AlertTriangle } from "@/components/icons";
import {
  type PricingFormValues, valuesToPricebook, formPricingMode, validatePricingForm,
} from "@/lib/pricingForm";
import {
  managementValue, saveBlocked, tariffValues, tariffWriteConfirmed, tenantCurrency, withTenantCurrency,
} from "@/lib/moneyManagement";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

interface TariffEditorProps {
  unitId: string;
  onSuccess?: () => void;
  inline?: boolean;
}

/**
 * Charger DEFAULT-tariff editor. Uses the ONE shared <PricingForm> + lib/pricingForm
 * serialization — identical fields/validation/pricebook to the Pricing Profiles form.
 * The ONLY difference is the save target: this writes the charger's default pricebook.
 *
 * The form starts from the exact persisted tariff (money contract 2). An edited draft
 * survives refetches and language changes; it is replaced by the saved tariff only
 * after a confirmed save of that same draft. A new (unset) tariff takes the operating
 * currency of the unit's tenant from the same answer; a saved tariff keeps its own. The
 * latest answer decides whether the currency may be saved, so a draft is kept but
 * blocked when the tenant no longer allows it. A save belongs to the unit and draft it
 * was sent for, even when the answer arrives after the user moved to another unit or
 * kept editing.
 */
export function TariffEditor({ unitId, onSuccess, inline }: TariffEditorProps) {
  const t = useT();
  const qc = useQueryClient();
  const tariffQ = useQuery({
    queryKey: ["unit", unitId, "tariff"],
    queryFn: () => fetchTariff(unitId),
    enabled: !!unitId,
  });
  const management = managementValue(tariffQ.data);
  const loaded = management?.ok ? management.value : null;
  const tenant = loaded ? tenantCurrency(loaded.operating_currency, loaded.currency_choices) : null;

  const baseline = loaded ? tariffValues(loaded.tariff) : null;
  // A draft belongs to the unit it was edited for.
  const [edited, setEdited] = useState<{ unitId: string; values: PricingFormValues } | null>(null);
  const draft = edited?.unitId === unitId ? edited.values : null;
  const setDraft = (values: PricingFormValues) => setEdited({ unitId, values });
  const shown = draft ?? (baseline?.ok ? baseline.value : null);
  const form = shown && tenant ? withTenantCurrency(shown, tenant) : null;
  const currentUnit = useRef(unitId);
  currentUnit.current = unitId;

  const mutation = useMutation({
    mutationFn: ({ unitId: origin, values }: { unitId: string; draft: PricingFormValues | null; values: PricingFormValues }) =>
      updateTariffPricebook(origin, formPricingMode(values) === "free" ? "free" : "paid", valuesToPricebook(values)),
    onSuccess: (written, { unitId: origin, draft: sentDraft, values }) => {
      if (!tariffWriteConfirmed(written, values.currency)) {
        toast.error(t("billing.tariffs.change_unconfirmed"));
        qc.invalidateQueries({ queryKey: ["unit", origin, "tariff"] });
        return;
      }
      toast.success(t("billing.tariffs.updated"));
      // The answer projects the tariff just written; the refetch confirms it.
      qc.setQueryData(["unit", origin, "tariff"], written);
      qc.invalidateQueries({ queryKey: ["unit", origin, "tariff"] });
      // Only the draft that was sent is replaced; a newer edit or another unit's draft stays.
      setEdited((cur) => (cur?.unitId === origin && cur.values === sentDraft ? null : cur));
      if (currentUnit.current === origin) onSuccess?.();
      else qc.invalidateQueries({ queryKey: ["unit", origin], exact: true });
    },
    onError: (e) => toast.error(errorMessage(t, e, { family: "unit_settings", fallback: "billing.tariffs.update_failed" })),
  });
  const saving = mutation.isPending && mutation.variables?.unitId === unitId;

  // The form's own validation message is shown as is; only request failures go through errorMessage.
  const save = () => {
    if (!form || !tenant || saveBlocked(form, tenant)) return;
    const err = validatePricingForm(form, t);
    if (err) { toast.error(err); return; }
    mutation.mutate({ unitId, draft, values: form });
  };

  if (tariffQ.isLoading) return <div className="text-sm opacity-60">{t("billing.tariffs.loading")}</div>;
  if (tariffQ.isError) return <div className="text-sm text-destructive">{t("billing.tariffs.load_failed")}</div>;

  const unavailable = management && !management.ok ? management.reason : baseline && !baseline.ok ? baseline.reason : null;

  const content = (
    <div className="flex flex-col gap-3">
      <p className="text-xs text-muted-foreground">
        {t("billing.tariffs.default_rate_help")}
      </p>
      {form && tenant ? (
        <>
          <PricingForm value={form} onChange={setDraft} tenant={tenant} idPrefix="tariff" />
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving || saveBlocked(form, tenant)}>
              {saving ? t("common.state.saving") : t("common.action.save")}
            </Button>
          </div>
        </>
      ) : (
        <Alert data-testid="tariff-unavailable">
          <AlertTriangle />
          <AlertDescription>{t("billing.tariffs.stored_unavailable", { reason: unavailable ?? "unavailable" })}</AlertDescription>
        </Alert>
      )}
    </div>
  );

  if (inline) return <div className="flex flex-col gap-4 border-t pt-4">{content}</div>;

  return (
    <Card>
      <CardHeader><CardTitle>{t("billing.tariffs.title")}</CardTitle></CardHeader>
      <CardContent className="flex-1 flex flex-col gap-4">{content}</CardContent>
    </Card>
  );
}
